import { FC, useEffect, useRef } from 'react'

import { ScopeProvider } from 'jotai-scope'
import { useAtomValue, useSetAtom } from 'jotai'

import {
  IconLightningBoltOff,
  EmptyState as EmptyStateComponent,
  Center
} from '@sequencehq/core-components'

import { ConfigFormTemplate } from 'Automations/nodeDefinitions/components'
import { getArrayItemIdFromFieldPath } from 'Automations/nodeDefinitions/validation/helpers'
import {
  focusedSwitchGroupAtom,
  selectedNodeFieldAtom
} from 'Automations/automationEditor/atoms'
import { ConfigFormProps } from '../../types'
import { useNode } from '../../hooks'
import { Config } from '.'
import { autoOpenConditionIdAtom, selectedGroupIdAtom } from './switch.atoms'
import { GroupEditor, GroupMenu } from './components'
import { useNodeFieldGroupSync } from './hooks/useNodeFieldGroupSync'
import { useGroupEditing, useGroups } from './hooks'
import type { SwitchEmptyStateReason } from './hooks/useGroupEditing/useGroupEditing'

export const SwitchConfigForm: FC<ConfigFormProps> = ({ nodeId }) => {
  const { nodeData } = useNode<Config>({ nodeId })
  const selectedField = useAtomValue(selectedNodeFieldAtom)

  if (!nodeData) return null

  const selectedGroupId =
    selectedField?.nodeId === nodeId
      ? getArrayItemIdFromFieldPath(selectedField.fieldId, 'groups')
      : undefined

  const isValidGroup = nodeData.groups.some(({ id }) => id === selectedGroupId)
  const initialGroupId = isValidGroup
    ? selectedGroupId
    : nodeData.groups.at(0)?.id

  // Pass the raw selected group ID (including non-group handles like 'ELSE')
  // so the child can set the initial focused group for edge highlighting.
  const initialFocusedGroup = selectedGroupId ?? initialGroupId

  return (
    <ScopeProvider
      atoms={[
        [selectedGroupIdAtom, initialGroupId],
        [autoOpenConditionIdAtom, undefined]
      ]}
    >
      <SwitchConfigFormContent
        nodeId={nodeId}
        initialFocusedGroup={initialFocusedGroup}
      />
    </ScopeProvider>
  )
}

const SwitchConfigFormContent: FC<{
  nodeId: string
  initialFocusedGroup: string | undefined
}> = ({ nodeId, initialFocusedGroup }) => {
  const { nodeData, definition } = useNode<Config>({ nodeId })
  const { emptyStateReason } = useGroupEditing({ nodeId })
  const { groups, addGroup } = useGroups({ nodeId })
  const selectedGroupId = useAtomValue(selectedGroupIdAtom)
  const setFocusedSwitchGroup = useSetAtom(focusedSwitchGroupAtom)
  const initialFocusedGroupRef = useRef(initialFocusedGroup)

  useNodeFieldGroupSync(nodeId)

  // On mount, set the focused group from the raw parent value (which may
  // be a non-group handle like 'ELSE') so the correct edge highlights.
  useEffect(() => {
    setFocusedSwitchGroup(initialFocusedGroupRef.current)
  }, [setFocusedSwitchGroup])

  // After mount, sync focused group with the selected group in the form.
  // Uses a ref to skip the initial render (handled by the mount effect above).
  const isInitialRender = useRef(true)
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false
      return
    }
    setFocusedSwitchGroup(selectedGroupId)
  }, [selectedGroupId, setFocusedSwitchGroup])

  useEffect(() => {
    return () => setFocusedSwitchGroup(undefined)
  }, [setFocusedSwitchGroup])

  if (!nodeData || !definition) return null

  const hasMultipleGroups = groups.length > 1

  return (
    <ConfigFormTemplate
      leadingContent={
        hasMultipleGroups ? <GroupMenu nodeId={nodeId} /> : undefined
      }
      minHeight={hasMultipleGroups ? '350px' : undefined}
    >
      <ConfigFormTemplate.Header nodeId={nodeId} />

      {emptyStateReason ? (
        <EmptyState reason={emptyStateReason} />
      ) : (
        <GroupEditor
          nodeId={nodeId}
          onAddGroup={!hasMultipleGroups ? addGroup : undefined}
        />
      )}
    </ConfigFormTemplate>
  )
}

const EMPTY_STATE_COPY: Record<
  SwitchEmptyStateReason,
  { title: string; body: string }
> = {
  NOT_CONNECTED: {
    title: 'Connect node',
    body: 'You need to connect this switch node to a start node before you can use it'
  },
  START_NODE_TRIGGER_UNSET: {
    title: 'Select start node type',
    body: 'You need to select a type on the start node before you can use this switch node'
  },
  START_NODE_TRIGGER_NO_CONDITIONS: {
    title: 'No conditions available',
    body: "The start node type doesn't provide any conditions for use with this switch node"
  }
}

const EmptyState: FC<{ reason: SwitchEmptyStateReason }> = ({ reason }) => {
  const { title, body } = EMPTY_STATE_COPY[reason]

  return (
    <Center
      flex="1"
      data-testid="automations.editor.configForm.switch.emptyState"
    >
      <EmptyStateComponent
        icon={IconLightningBoltOff}
        iconSize="sm"
        title={title}
        body={body}
        padding="16px 0"
        minHeight="initial"
      />
    </Center>
  )
}
